import { Navigate } from "react-router-dom";

import { AppPagePaths } from "App/routes/paths";
import { campaignsRoute } from "Pages/orchestration/Pages/campaigns/Pages/CampaignsRoutes";
import { customFunctionRoute } from "Pages/orchestration/Pages/customFunction/customFunctionRouter";
import { ivrRoute } from "Pages/orchestration/Pages/ivrs/Pages/IVRRoutes";
import { journeysRoute } from "Pages/orchestration/Pages/journeys/Routes";
import { orchestrationRoutingRulesRoute } from "Pages/orchestration/Pages/taskRouting/Pages/Routes";

import OrchestrationRoutingPage from "./OrchestrationRoutingPage";
import { orchestrationPathResources } from "./Pages/orchestrationPaths";

import type { RegalRouteObject } from "App/routes/types";

export type OrchestrationFlags = {
    showJourneys: boolean;
    showCampaigns: boolean;
    showIVR: boolean;
    showTaskRouting: boolean;
    showCustomFunctions?: boolean;
};

/**
 * Determines the default redirect path based on available feature flags.
 * Returns the first available page in priority order.
 */
export const getDefaultOrchestrationPath = (flags: OrchestrationFlags): string => {
    const { showJourneys, showCampaigns, showIVR, showTaskRouting } = flags;

    // Priority order matches the navigation menu order
    if (showJourneys) {
        return `${AppPagePaths.orchestration}/${orchestrationPathResources.journeys}`;
    }
    if (showCampaigns) {
        return `${AppPagePaths.orchestration}/${orchestrationPathResources.campaigns}`;
    }
    if (showIVR) {
        return `${AppPagePaths.orchestration}/${orchestrationPathResources.ivrs}`;
    }
    if (showTaskRouting) {
        return `${AppPagePaths.orchestration}/${orchestrationPathResources.routing}`;
    }
    if (flags.showCustomFunctions) {
        return `${AppPagePaths.orchestration}/${orchestrationPathResources.customFunctions}`;
    }

    // Fallback to root
    return `/`;
};

export const getOrchestrationRoute = (flags: OrchestrationFlags): RegalRouteObject => {
    const defaultPath = getDefaultOrchestrationPath(flags);

    return {
        path: AppPagePaths.orchestration,
        element: <OrchestrationRoutingPage />,
        hasNestedRoutes: true,
        children: [
            {
                index: true,
                element: <Navigate to={defaultPath} replace />,
            },
            {
                path: orchestrationPathResources.journeys,
                element: journeysRoute.element,
                children: journeysRoute.children,
            },
            {
                path: orchestrationPathResources.campaigns,
                element: campaignsRoute.element,
                children: campaignsRoute.children,
            },
            {
                path: orchestrationPathResources.ivrs,
                element: ivrRoute.element,
                children: ivrRoute.children,
            },
            {
                path: orchestrationPathResources.routing,
                element: orchestrationRoutingRulesRoute.element,
                children: orchestrationRoutingRulesRoute.children,
            },
            {
                path: customFunctionRoute.path,
                element: customFunctionRoute.element,
                children: customFunctionRoute.children,
            },
        ],
    };
};
